/**
 * ContextWindow — Generation context management for web app.
 * Mirrors Tauri ContextWindow for feature parity.
 */
import { useState, useMemo, useCallback } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useAppStore } from '@/store/appStore';
import { cn } from '@/lib/utils';
import {
  TreePine,
  Bot,
  Image as ImageIcon,
  Plus,
  X,
  ChevronDown,
  ChevronRight,
  Sparkles,
} from 'lucide-react';

interface ContextNote {
  id: string;
  text: string;
  author: 'user' | 'ai';
  timestamp: string;
}

interface ContextEntity {
  entityId: string;
  selected: boolean;
  notes: ContextNote[];
  assets: { assetId: string; selected: boolean; notes: ContextNote[] }[];
  aspects: {
    bio: boolean;
    socialLinks: boolean;
    brandColors: boolean;
    contactInfo: boolean;
    metadata: boolean;
  };
}

interface GenerationContext {
  notes: ContextNote[];
  entities: ContextEntity[];
  globalAssets: { assetId: string; selected: boolean; notes: ContextNote[] }[];
}

export function ContextWindow() {
  const { currentProjectId } = useAppStore();
  const accountId = useAppStore((s) => s.currentAccountId) ?? undefined;
  
  const project = useQuery(
    api.projects.get,
    currentProjectId ? { accountId, id: currentProjectId } : 'skip'
  );
  const entities = useQuery(api.entities.list, { accountId });
  const assets = useQuery(api.assets.list, { accountId });

  const [context, setContext] = useState<GenerationContext>({
    notes: [],
    entities: [],
    globalAssets: [],
  });
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');

  // Build context from project data
  useMemo(() => {
    if (!entities) return;
    
    const contextEntities: ContextEntity[] = entities.map((entity) => {
      const entityAssets = (assets || []).filter(
        (a) => a.ownerEntityId === entity._id
      );
      
      return {
        entityId: entity._id,
        selected: true,
        notes: [],
        assets: entityAssets.map((a) => ({
          assetId: a._id,
          selected: entityAssets.length === 1,
          notes: [],
        })),
        aspects: {
          bio: true,
          socialLinks: true,
          brandColors: true,
          contactInfo: false,
          metadata: false,
        },
      };
    });

    setContext((prev) => ({
      ...prev,
      entities: contextEntities,
      globalAssets: (assets || [])
        .filter((a) => !a.ownerEntityId)
        .map((a) => ({
          assetId: a._id,
          selected: false,
          notes: [],
        })),
    }));
  }, [entities, assets]);

  const toggleExpanded = (id: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleEntitySelected = (entityId: string) => {
    setContext((prev) => ({
      ...prev,
      entities: prev.entities.map((e) =>
        e.entityId === entityId ? { ...e, selected: !e.selected } : e
      ),
    }));
  };

  const toggleAssetSelected = (entityId: string | null, assetId: string) => {
    if (entityId) {
      setContext((prev) => ({
        ...prev,
        entities: prev.entities.map((e) =>
          e.entityId === entityId
            ? {
                ...e,
                assets: e.assets.map((a) =>
                  a.assetId === assetId ? { ...a, selected: !a.selected } : a
                ),
              }
            : e
        ),
      }));
    } else {
      setContext((prev) => ({
        ...prev,
        globalAssets: prev.globalAssets.map((a) =>
          a.assetId === assetId ? { ...a, selected: !a.selected } : a
        ),
      }));
    }
  };

  const toggleEntityAspect = (
    entityId: string,
    aspect: keyof ContextEntity['aspects']
  ) => {
    setContext((prev) => ({
      ...prev,
      entities: prev.entities.map((e) =>
        e.entityId === entityId
          ? {
              ...e,
              aspects: { ...e.aspects, [aspect]: !e.aspects[aspect] },
            }
          : e
      ),
    }));
  };

  const addNote = () => {
    if (!newNoteText.trim()) return;

    const note: ContextNote = {
      id: crypto.randomUUID(),
      text: newNoteText.trim(),
      author: 'user',
      timestamp: new Date().toISOString(),
    };

    setContext((prev) => ({ ...prev, notes: [...prev.notes, note] }));
    setNewNoteText('');
    setShowNoteInput(false);
  };

  const selectedCount = useMemo(() => {
    return (
      context.entities.filter((e) => e.selected).length +
      context.entities.reduce(
        (sum, e) => sum + e.assets.filter((a) => a.selected).length,
        0
      ) +
      context.globalAssets.filter((a) => a.selected).length
    );
  }, [context]);

  if (!project) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-4 text-center">
        <TreePine className="h-10 w-10 text-muted-foreground/50 mb-2" />
        <p className="text-sm text-muted-foreground">No project selected</p>
        <p className="text-xs text-muted-foreground mt-1">
          Select a project to manage context
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-card">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border p-3">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-sm flex items-center gap-2">
            <Bot className="h-4 w-4 text-primary" />
            Generation Context
          </h3>
          <span className="text-xs text-muted-foreground">
            {selectedCount} selected
          </span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Choose what information to include in AI generation
        </p>
      </div>

      {/* Context Tree */}
      <div className="flex-1 overflow-auto p-3 space-y-2">
        {/* Global Notes */}
        <div className="rounded-lg border border-border">
          <button
            onClick={() => toggleExpanded('root')}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-muted/50"
          >
            {expandedItems.has('root') ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            <span>Notes</span>
            {context.notes.length > 0 && (
              <span className="ml-auto text-xs text-muted-foreground">
                {context.notes.length}
              </span>
            )}
          </button>
          
          {expandedItems.has('root') && (
            <div className="px-3 pb-3 space-y-2">
              {context.notes.map((note) => (
                <div
                  key={note.id}
                  className="text-sm bg-muted/50 rounded p-2"
                >
                  <p>{note.text}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {new Date(note.timestamp).toLocaleString()}
                  </p>
                </div>
              ))}
              
              {showNoteInput ? (
                <div className="space-y-2">
                  <textarea
                    value={newNoteText}
                    onChange={(e) => setNewNoteText(e.target.value)}
                    placeholder="Add a note..."
                    className="w-full rounded border border-input bg-background px-2 py-1.5 text-sm resize-none"
                    rows={2}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={addNote}
                      className="px-2 py-1 bg-primary text-primary-foreground rounded text-xs"
                    >
                      Add
                    </button>
                    <button
                      onClick={() => {
                        setShowNoteInput(false);
                        setNewNoteText('');
                      }}
                      className="px-2 py-1 border rounded text-xs"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowNoteInput(true)}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  <Plus className="h-3 w-3" />
                  Add note
                </button>
              )}
            </div>
          )}
        </div>

        {/* Entities */}
        {entities?.map((entity) => {
          const ctxEntity = context.entities.find(
            (e) => e.entityId === entity._id
          );
          if (!ctxEntity) return null;

          return (
            <EntityContextItem
              key={entity._id}
              entity={entity}
              ctxEntity={ctxEntity}
              expanded={expandedItems.has(entity._id)}
              onToggle={() => toggleExpanded(entity._id)}
              onToggleSelected={() => toggleEntitySelected(entity._id)}
              onToggleAspect={(aspect) => toggleEntityAspect(entity._id, aspect)}
              onToggleAsset={(assetId) =>
                toggleAssetSelected(entity._id, assetId)
              }
              assets={assets?.filter((a) => a.ownerEntityId === entity._id)}
            />
          );
        })}

        {/* Global Assets */}
        {context.globalAssets.length > 0 && (
          <div className="rounded-lg border border-border">
            <button
              onClick={() => toggleExpanded('global-assets')}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-muted/50"
            >
              {expandedItems.has('global-assets') ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              <ImageIcon className="h-4 w-4" />
              <span>Unassigned Assets</span>
            </button>
            
            {expandedItems.has('global-assets') && (
              <div className="px-3 pb-2 space-y-1">
                {context.globalAssets.map((assetCtx) => {
                  const asset = assets?.find((a) => a._id === assetCtx.assetId);
                  if (!asset) return null;
                  return (
                    <label
                      key={asset._id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={assetCtx.selected}
                        onChange={() => toggleAssetSelected(null, asset._id)}
                        className="rounded border-input"
                      />
                      <span className="text-sm truncate">{asset.name}</span>
                      <span className="text-xs text-muted-foreground ml-auto">
                        {asset.type}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-border p-3">
        <button className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
          <Sparkles className="h-4 w-4" />
          Generate with Context
        </button>
      </div>
    </div>
  );
}

interface EntityContextItemProps {
  entity: {
    _id: string;
    name: string;
    displayName?: string;
    entityType: string;
  };
  ctxEntity: ContextEntity;
  expanded: boolean;
  onToggle: () => void;
  onToggleSelected: () => void;
  onToggleAspect: (aspect: keyof ContextEntity['aspects']) => void;
  onToggleAsset: (assetId: string) => void;
  assets?: Array<{
    _id: string;
    name: string;
    type: string;
  }>;
}

function EntityContextItem({
  entity,
  ctxEntity,
  expanded,
  onToggle,
  onToggleSelected,
  onToggleAspect,
  onToggleAsset,
  assets,
}: EntityContextItemProps) {
  const typeIcon =
    entity.entityType === 'venue'
      ? '🏢'
      : entity.entityType === 'artist'
      ? '🎤'
      : '👤';

  return (
    <div className="rounded-lg border border-border">
      <div className="flex items-center gap-2 px-3 py-2">
        <button onClick={onToggle} className="p-0.5 hover:bg-muted/50 rounded">
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>
        <label className="flex items-center gap-2 flex-1 cursor-pointer">
          <input
            type="checkbox"
            checked={ctxEntity.selected}
            onChange={onToggleSelected}
            className="rounded border-input"
          />
          <span>{typeIcon}</span>
          <span className="text-sm font-medium truncate">
            {entity.displayName || entity.name}
          </span>
        </label>
      </div>

      {expanded && ctxEntity.selected && (
        <div className="px-3 pb-3 space-y-2 border-t border-border mt-1 pt-2">
          {/* Aspects */}
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Include:</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(ctxEntity.aspects).map(([key, value]) => (
                <label
                  key={key}
                  className="flex items-center gap-1 text-xs cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={() =>
                      onToggleAspect(key as keyof ContextEntity['aspects'])
                    }
                    className="rounded border-input"
                  />
                  <span className="capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Assets */}
          {assets && assets.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Assets:</p>
              <div className="space-y-1">
                {assets.map((asset) => {
                  const assetCtx = ctxEntity.assets.find(
                    (a) => a.assetId === asset._id
                  );
                  return (
                    <label
                      key={asset._id}
                      className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted/50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={assetCtx?.selected ?? false}
                        onChange={() => onToggleAsset(asset._id)}
                        className="rounded border-input"
                      />
                      <span className="text-xs truncate">{asset.name}</span>
                      <span className="text-[10px] text-muted-foreground ml-auto">
                        {asset.type}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
